import { useEffect, useState } from 'react';
import { Plus, Pencil, Power } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useFinance } from '@/contexts/FinanceContext';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import type { FinanceCostCenter } from '@/types/finance';

const COLORS = [
  '#113554',
  '#1e40af',
  '#059669',
  '#d97706',
  '#dc2626',
  '#7c3aed',
  '#0891b2',
  '#db2777',
  '#65a30d',
  '#f37b53',
];

interface CostCenterFormProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editCostCenter?: FinanceCostCenter;
}

function CostCenterModal({ open, onOpenChange, editCostCenter }: CostCenterFormProps) {
  const { addCostCenter, updateCostCenter } = useFinance();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(COLORS[0]!);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    if (editCostCenter) {
      setName(editCostCenter.name);
      setDescription(editCostCenter.description ?? '');
      setColor(editCostCenter.color);
    } else {
      setName('');
      setDescription('');
      setColor(COLORS[0]!);
    }
  }, [open, editCostCenter]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    if (editCostCenter) {
      updateCostCenter(editCostCenter.id, { name, ...(description ? { description } : {}), color });
    } else {
      addCostCenter({ name, ...(description ? { description } : {}), color, isActive: true });
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="font-brand">
            {editCostCenter ? 'Editar Centro de Custo' : 'Novo Centro de Custo'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="mt-2 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="cc-name">Nome *</Label>
            <Input
              id="cc-name"
              placeholder="Ex: Clínica Principal"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cc-desc">Descrição</Label>
            <Textarea
              id="cc-desc"
              placeholder="Descrição opcional"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Cor</Label>
            <div className="flex flex-wrap gap-2">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`h-8 w-8 rounded-full border-2 transition-all ${color === c ? 'scale-110 border-foreground' : 'border-transparent hover:scale-105'}`}
                  style={{ backgroundColor: c }}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit">{editCostCenter ? 'Salvar' : 'Criar'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function CostCentersPage() {
  const { clientId } = useCurrentClient();
  const { costCenters, deleteCostCenter, isLoading } = useFinance();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCC, setEditingCC] = useState<FinanceCostCenter | undefined>();

  if (!clientId) {
    return (
      <div className="rounded-xl border border-border bg-white p-8 text-center text-muted-foreground">
        <p className="text-sm">Selecione um cliente para gerenciar os centros de custo.</p>
      </div>
    );
  }

  const active = costCenters.filter((cc) => cc.isActive);
  const inactive = costCenters.filter((cc) => !cc.isActive);

  const openCreate = () => {
    setEditingCC(undefined);
    setModalOpen(true);
  };
  const openEdit = (cc: FinanceCostCenter) => {
    setEditingCC(cc);
    setModalOpen(true);
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-brand text-2xl font-bold text-foreground">Centros de Custo</h1>
          <p className="text-sm text-muted-foreground">
            {active.length} ativo{active.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-accent font-semibold text-accent-foreground hover:bg-accent/90"
        >
          <Plus size={16} className="mr-2" />
          Novo Centro
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 animate-pulse rounded-lg border bg-muted" />
          ))}
        </div>
      ) : costCenters.length === 0 ? (
        <div className="rounded-xl border border-border bg-white p-12 text-center text-muted-foreground">
          <p className="font-medium">Nenhum centro de custo cadastrado</p>
          <p className="mt-1 text-sm">
            Centros de custo ajudam a categorizar gastos por área ou projeto.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {active.length > 0 && (
            <div>
              <h2 className="mb-3 text-sm font-semibold text-muted-foreground">Ativos</h2>
              <div className="divide-y rounded-xl border border-border bg-white">
                {active.map((cc) => (
                  <div key={cc.id} className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                      <div className="h-4 w-4 rounded-full" style={{ backgroundColor: cc.color }} />
                      <div>
                        <p className="text-sm font-medium">{cc.name}</p>
                        {cc.description && (
                          <p className="text-xs text-muted-foreground">{cc.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => openEdit(cc)}
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => deleteCostCenter(cc.id)}
                        title="Desativar"
                      >
                        <Power size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {inactive.length > 0 && (
            <div>
              <h2 className="mb-3 text-sm font-semibold text-muted-foreground">Inativos</h2>
              <div className="divide-y rounded-xl border border-border bg-white opacity-60">
                {inactive.map((cc) => (
                  <div key={cc.id} className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="h-4 w-4 rounded-full grayscale"
                        style={{ backgroundColor: cc.color }}
                      />
                      <div>
                        <p className="text-sm font-medium text-muted-foreground line-through">
                          {cc.name}
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      Inativo
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <CostCenterModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        {...(editingCC ? { editCostCenter: editingCC } : {})}
      />
    </div>
  );
}
